<template>
  <Grid
    class="trip-visits-grid"
    :data-items="tripVisits"
    :columns="columns"
    :loader="loading"
    data-cy="trip-visits-grid"
    ref="gridRef"
  >
    <template v-slot:DeleteCell="{ props: { dataItem } }">
      <td class="k-table-td">
        <font-awesome-icon
          class="delete-icon"
          @click="deleteVisitAsync(dataItem)"
          icon="times"
        />
      </td>
    </template>
  </Grid>
</template>

<script setup lang="ts">
import { api } from "@/api";
import type { GridColumnPropsExtended } from "@/Domain/GridColumnPropsExtended";
import type { TripVisitDisplay } from "@/Domain/TripVisitDisplay";
import { handleResponsePromiseAsync } from "@/shared/helpers/apiHelper";
import { ref, watchEffect } from "vue";
import { Grid } from "@progress/kendo-vue-grid";
import GridController from "@/shared/services/GridController";

const props = defineProps<{
  tripId: number;
  tripVisits: TripVisitDisplay[];
  loading: boolean;
}>();

const emit = defineEmits<{
  (event: "visitDeleted", visitId: number): void;
}>();

const gridRef = ref<InstanceType<typeof Grid> | null>(null);

const columns = ref([
  {
    title: "Delete",
    cell: "DeleteCell",
    minWidth: 50,
  },
  {
    field: "VisitId",
    title: "Visit ID",
    minWidth: 70,
  },
  {
    field: "VisitType",
    title: "Type",
    minWidth: 70,
  },
  {
    field: "VisitDate",
    title: "Visit Date",
    type: "date",
    format: "{0:MM/dd/yyyy}",
    minWidth: 100,
  },
  {
    field: "Plant",
    title: "Plant",
    minWidth: 100,
  },
  {
    field: "Company",
    title: "Company",
    minWidth: 100,
  },
  {
    field: "Rc",
    title: "RC",
    minWidth: 100,
  },
] as GridColumnPropsExtended[]);

const controller = new GridController<TripVisitDisplay>(
  "TripVisitsGrid",
  columns.value
);
controller.initOptions();

const deleteVisitAsync = async function (visit: TripVisitDisplay) {
  return handleResponsePromiseAsync(
    api.post("DeleteTripVisit", {
      TripId: props.tripId,
      VisitId: visit.VisitId,
    }),
    () => {
      emit("visitDeleted", visit.VisitId);
    },
    "TripVisitsGrid.vue",
    "deleteVisitAsync"
  );
};

watchEffect(() => {
  if (gridRef.value && !controller.gridRef.value) {
    controller.setGridRef(gridRef);
  }
});
</script>

<style scoped lang="scss">
.trip-visits-grid {
  min-height: 200px;
  .delete-icon {
    cursor: pointer;
    color: red;

    &:hover {
      color: darkred;
    }
  }
}
</style>
